/**
 * @file AdminLayout.tsx
 *
 * Navigation + routed content for the Online Shop admin: the Gravity sidebar on
 * desktop, a section switcher on smaller viewports, and the active route's screen
 * in the scrolling content area via react-router's {@link Outlet}. The fixed app
 * shell and the top bar live one level up in {@link AppShell}. It also loads the
 * shared currency symbol once for every tab on mount.
 */

import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useResponsive } from '../hooks/use-responsive';
import { useSettingsStore } from '../stores/settings.store';
import { AdminLayoutContentWidth } from '../layout-constants';
import { MobileNav } from './MobileNav';
import { SideNav } from './SideNav';
import styles from './AdminLayout.module.css';

/**
 * The responsive admin shell: navigation plus the routed content area.
 *
 * @returns The shop admin layout.
 */
export function AdminLayout() {
  const { isDesktop } = useResponsive();
  const fetchCurrencySymbol = useSettingsStore((s) => s.fetchCurrencySymbol);

  // Load the shared currency symbol once for the whole admin; every tab reads
  // it from the settings store rather than fetching its own copy.
  useEffect(() => {
    fetchCurrencySymbol();
  }, [fetchCurrencySymbol]);

  return (
    <>
      {!isDesktop && <MobileNav />}
      <div className={`gv-flex gv-flex-row ${styles.mainRow}`}>
        {isDesktop && <SideNav />}
        <div className={styles.content}>
          <div
            className="gv-w-full gv-mx-auto"
            style={{ maxWidth: AdminLayoutContentWidth }}
          >
            <Outlet />
          </div>
        </div>
      </div>
    </>
  );
}
